import { Box, Stack, TextField, Typography } from "@mui/material";
import type { SchemeManifest, SchemeTokenOverrides } from "@shared/types/foyer";
import { useTranslation } from "react-i18next";
import ColorSwatch from "../ColorSwatch";
import { fieldGrid } from "./editorLayout";

interface Props {
  manifest: SchemeManifest;
  value: SchemeTokenOverrides | undefined;
  onChange: (next: SchemeTokenOverrides) => void;
}

/**
 * Manifest-driven palette + font editor. Renders one control per token key the
 * template declares in `tokenSchema`, seeded from `defaultTokens`. The premise
 * stores only the overrides (a sparse diff); clearing a field back to the
 * default is achieved by typing the default value (kept simple for v1).
 */
export default function DesignTokenEditor({
  manifest,
  value,
  onChange,
}: Props) {
  const { t } = useTranslation("templates");
  const defaults = manifest.defaultTokens;
  const palDefaults = defaults.palette as Record<string, string>;
  const exDefaults = (defaults.extras ?? {}) as Record<string, string>;

  const setPalette = (key: string, v: string) =>
    onChange({ ...value, palette: { ...value?.palette, [key]: v } });
  const setExtra = (key: string, v: string) =>
    onChange({ ...value, extras: { ...value?.extras, [key]: v } });

  const paletteVal = (key: string) =>
    (value?.palette as Record<string, string> | undefined)?.[key] ??
    palDefaults[key] ??
    "";
  const extraVal = (key: string) =>
    value?.extras?.[key] ?? exDefaults[key] ?? "";

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          {t("designTokenEditor.palette")}
        </Typography>
        <Box sx={fieldGrid(260)}>
          {(manifest.tokenSchema?.palette ?? []).map(({ key, label }) => (
            <ColorRow
              key={key}
              label={label}
              value={paletteVal(key)}
              onChange={(v) => setPalette(key, v)}
            />
          ))}
        </Box>
      </Box>

      {(manifest.tokenSchema?.extras ?? []).length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t("designTokenEditor.accents")}
          </Typography>
          <Box sx={fieldGrid(260)}>
            {(manifest.tokenSchema?.extras ?? []).map(({ key, label }) => (
              <ColorRow
                key={key}
                label={label}
                value={extraVal(key)}
                onChange={(v) => setExtra(key, v)}
              />
            ))}
          </Box>
        </Box>
      )}
    </Stack>
  );
}

/** A label + color swatch + raw value field (the field also handles rgba()). */
function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <ColorSwatch value={value} onChange={onChange} ariaLabel={label} />
      <TextField
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ flex: 1 }}
      />
    </Stack>
  );
}
